import React, { useRef, useState } from 'react'

import { AttachmentButton } from '../attachment-button'
import { Box } from '../box'
import { Pressable } from '../pressable'
import { CloseGlyph, GLYPH_COLORS } from '../primitive-glyphs/glyphs'
import { Spinner } from '../spinner'
import { Text } from '../text'
import {
    AttachmentPreviewModal,
    type AttachmentReplacement,
    isImageAttachment,
    type PreviewAttachment,
} from './AttachmentPreviewModal.web'

export type AttachmentGridProps<T extends PreviewAttachment> = {
    attachments: T[]
    isUploading?: boolean
    /** Format hint under the upload tile's label. */
    hint?: string
    /** `accept` of the file input, e.g. `.jpg,.png,.pdf`. */
    accept?: string
    /** Shows the upload tile. */
    onUpload?(files: FileList): void
    /** Shows a remove button on every tile. */
    onRemove?(attachment: T): void
    /** Makes previewed images rotatable. */
    onReplace?(payload: AttachmentReplacement): void
}

const tile =
    'size-[124px] overflow-hidden rounded border border-solid border-border-subtle bg-background-elevation'

/**
 * 124px attachment tiles with a full-screen preview, plus the upload tile and
 * per-tile remove when the matching handlers are passed. Web only.
 */
export const AttachmentGrid = <T extends PreviewAttachment>({
    attachments,
    isUploading,
    hint,
    accept,
    onUpload,
    onRemove,
    onReplace,
}: AttachmentGridProps<T>) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [previewIndex, setPreviewIndex] = useState(0)
    const [isPreviewOpen, setPreviewOpen] = useState(false)

    return (
        <Box>
            <Box className='flex-row flex-wrap gap-3'>
                {attachments.map((attachment, index) => (
                    <Box key={index} className={tile}>
                        <Pressable
                            className='h-full w-full items-center justify-center'
                            accessibilityRole='button'
                            accessibilityLabel={attachment.name ?? undefined}
                            onPress={() => {
                                setPreviewIndex(index)
                                setPreviewOpen(true)
                            }}
                        >
                            {isImageAttachment(attachment) && attachment.link ? (
                                <img
                                    src={attachment.link}
                                    alt={attachment.name ?? ''}
                                    className='h-full w-full object-cover'
                                />
                            ) : (
                                <Text
                                    size='caption'
                                    tone='muted'
                                    numberOfLines={3}
                                    className='px-2 text-center'
                                >
                                    {attachment.name ?? ''}
                                </Text>
                            )}
                        </Pressable>
                        {onRemove && (
                            <Pressable
                                className='absolute right-1 top-1 rounded bg-background-surface p-1 hover:bg-background-surface-hover'
                                accessibilityRole='button'
                                accessibilityLabel='Remove'
                                onPress={() => onRemove(attachment)}
                            >
                                <CloseGlyph size={16} color={GLYPH_COLORS.default} />
                            </Pressable>
                        )}
                    </Box>
                ))}

                {isUploading && (
                    <Box className={`items-center justify-center ${tile}`}>
                        <Spinner />
                    </Box>
                )}

                {onUpload && (
                    <div
                        data-testid='attachment-drop-zone'
                        onDragOver={event => event.preventDefault()}
                        onDrop={event => {
                            event.preventDefault()
                            if (event.dataTransfer.files.length) {
                                onUpload(event.dataTransfer.files)
                            }
                        }}
                    >
                        <AttachmentButton hint={hint} onPress={() => inputRef.current?.click()} />
                        <input
                            ref={inputRef}
                            type='file'
                            multiple
                            accept={accept}
                            hidden
                            onChange={event => event.target.files && onUpload(event.target.files)}
                        />
                    </div>
                )}
            </Box>

            <AttachmentPreviewModal
                isOpen={isPreviewOpen}
                attachments={attachments}
                attachmentIndex={previewIndex}
                editable={!!onReplace}
                closeAction={() => setPreviewOpen(false)}
                updateAttachment={onReplace}
                onIndexChange={setPreviewIndex}
            />
        </Box>
    )
}
